import type { ComplianceRequirementListView, ComplianceRequirementView } from "@solomon-apps/shared";

export type ComplianceRequirementStage = "missing" | "pending_review" | "pending_update" | "approved" | "rejected";

export interface ComplianceRequirementStatusCopy {
    stage: ComplianceRequirementStage;
    label: string;
    description: string;
    indicator: "success" | "pending" | "error" | "disabled";
    textClassName: string;
}

const STAGE_RANK: Record<ComplianceRequirementStage, number> = {
    rejected: 0,
    missing: 1,
    pending_review: 2,
    pending_update: 3,
    approved: 4
};

export function hasComplianceRequirements(requirements: ComplianceRequirementListView | null | undefined): boolean {
    return Boolean(requirements && (requirements.summary.requirementCount > 0 || requirements.requirements.length > 0));
}

export function getComplianceRequirementStage(requirement: ComplianceRequirementView): ComplianceRequirementStage {
    const hasRejectedReview = requirement.pendingReviews.some((review) => review.status === "rejected");
    if (hasRejectedReview) return "rejected";

    const hasPendingReview = requirement.pendingReviews.some((review) => review.status === "pending");
    if (requirement.approvedEvidenceVersion && hasPendingReview) {
        return "pending_update";
    }
    if (requirement.approvedEvidenceVersion || requirement.satisfied) {
        return "approved";
    }
    if (hasPendingReview) return "pending_review";
    return "missing";
}

export function complianceRequirementStatusCopy(
    requirement: ComplianceRequirementView
): ComplianceRequirementStatusCopy {
    const stage = getComplianceRequirementStage(requirement);
    switch (stage) {
        case "approved":
            return {
                stage,
                label: "Approved",
                description: "Current approved evidence satisfies this requirement.",
                indicator: "success",
                textClassName: "text-success-base"
            };
        case "pending_update":
            return {
                stage,
                label: "Update pending review",
                description:
                    "A newer evidence version is waiting for distributor review. The approved version remains on file.",
                indicator: "pending",
                textClassName: "text-warning-base"
            };
        case "pending_review":
            return {
                stage,
                label: "Pending distributor review",
                description: "Evidence has been submitted and is waiting for distributor review.",
                indicator: "pending",
                textClassName: "text-warning-base"
            };
        case "rejected":
            return {
                stage,
                label: "Action required",
                description: "The latest review needs an update before this requirement can be approved.",
                indicator: "error",
                textClassName: "text-error-base"
            };
        case "missing":
        default:
            return {
                stage: "missing",
                label: requirement.warningOnly ? "Requested" : "Missing evidence",
                description: requirement.warningOnly
                    ? "This soft requirement is requested but does not block writes."
                    : "Submit evidence so the distributor can review this hard requirement.",
                indicator: requirement.warningOnly ? "disabled" : "error",
                textClassName: requirement.warningOnly ? "text-foreground-muted" : "text-error-base"
            };
    }
}

export function sortComplianceRequirements(requirements: ComplianceRequirementView[]): ComplianceRequirementView[] {
    return [...requirements].sort((a, b) => {
        const byStage = STAGE_RANK[getComplianceRequirementStage(a)] - STAGE_RANK[getComplianceRequirementStage(b)];
        if (byStage !== 0) return byStage;
        if (a.gate !== b.gate) return a.gate === "hard" ? -1 : 1;
        return a.module.displayName.localeCompare(b.module.displayName);
    });
}

export function countRequirementsByStage(
    requirements: ComplianceRequirementView[]
): Record<ComplianceRequirementStage, number> {
    return requirements.reduce(
        (counts, requirement) => {
            counts[getComplianceRequirementStage(requirement)] += 1;
            return counts;
        },
        {
            missing: 0,
            pending_review: 0,
            pending_update: 0,
            approved: 0,
            rejected: 0
        } satisfies Record<ComplianceRequirementStage, number>
    );
}
